import { alpha, PaletteMode } from '@mui/material';

// Boomi Exosphere design tokens, named as in Exosphere (`--exo-palette-*`,
// `--exo-color-*`) and in the rebrand Figma file, so a token found in Figma is
// the same token here. Values match the `@boomi/exosphere` 8.1.4 package. The
// `.agents/skills/boomi-look` skill holds the decisions behind them.

export const exoPalette = {
  white: '#FFFFFF',
  black: '#000000',
  'gray-10': '#F5F5F5',
  'gray-20': '#E5E5E5',
  'gray-30': '#CCCCCC',
  'gray-40': '#ABABAB',
  'gray-50': '#8C8C8C',
  'gray-60': '#707070',
  'gray-70': '#454545',
  'gray-80': '#2E2E2E',
  'gray-90': '#1F1F1F',
  'gray-100': '#1A1A1A',
  'navy-10': '#CDD5DD',
  'navy-20': '#B5BFCC',
  'navy-30': '#9CAABB',
  'navy-40': '#8395AA',
  'navy-50': '#516B88',
  'navy-60': '#395577',
  'navy-70': '#204066',
  'navy-80': '#072B55',
  'blue-10': '#E5F3FD',
  'blue-20': '#B2DAFB',
  'blue-30': '#66B6F6',
  'blue-40': '#339DF3',
  'blue-50': '#0085F0',
  'blue-60': '#006CD6',
  'blue-70': '#0052BD',
  'blue-80': '#001F8A',
  'blue-90': '#152A40',
  'green-10': '#CFF3E8',
  'green-40': '#3ECFA2',
  'green-50': '#0EC38B',
  'green-60': '#00A972',
  'green-70': '#009058',
  'green-90': '#003D18',
  'yellow-20': '#FCEDBD',
  'yellow-40': '#EC9932',
  'yellow-50': '#CF761B',
  'yellow-60': '#A16123',
  'yellow-70': '#7B4E23',
  'yellow-80': '#4E2D0E',
  'red-10': '#FFEDF0',
  'red-30': '#FC92A7',
  'red-50': '#FF5C6C',
  'red-60': '#C71739',
  'red-70': '#940006',
  'red-90': '#43191B',
  'coral-50': '#FF7C66',
  'coral-70': '#B2301A',
  'purple-30': '#C684BD',
  'purple-40': '#B35BA7',
  'purple-50': '#A03291',
  'purple-60': '#861978',
  'periwinkle-30': '#9395EE',
  'periwinkle-40': '#6F72E8',
  'periwinkle-50': '#4B4FE2',
  'periwinkle-70': '#181CAF',
  'aqua-40': '#79BFBE',
  'aqua-70': '#22626F',
};

export const exoOpacity = {
  '1': 0.1,
  '2': 0.15,
  '2-1': 0.3,
  '3': 0.4,
  '4': 0.5,
  '5': 0.6,
  '6': 0.75,
  '7': 0.9,
  '8': 0.95,
};

const p = exoPalette;

const exoColorsLight = {
  page: p.white,
  background: p.white,
  'background-secondary': p['gray-10'],
  'background-tertiary': p['gray-20'],
  'background-tertiary-selected-weak': p['gray-20'],
  'background-brand': p['navy-80'],
  'background-action': p['navy-80'],
  'background-action-hover': p['navy-70'],
  'background-action-hover-weak': p['gray-10'],
  'background-action-inverse': p['gray-90'],
  'background-action-secondary': p.white,
  'background-action-secondary-hover': p['blue-10'],
  'background-disabled': p['gray-30'],
  'background-deselected': p['gray-60'],
  'background-selected': p['blue-60'],
  'background-selected-hover': p['blue-70'],
  'background-selected-weak': p['blue-10'],
  'background-highlight': p['coral-50'],
  'background-success': p['green-10'],
  'background-success-strong': p['green-70'],
  'background-warning': p['yellow-20'],
  'background-warning-strong': p['yellow-50'],
  'background-danger-weak': p['red-10'],
  'background-danger-strong': p['red-60'],
  'background-danger-strong-hover': p['red-70'],
  'background-info-strong': p['gray-60'],
  icon: p['navy-80'],
  'icon-disabled': p['gray-60'],
  'icon-inverse': p.white,
  font: p['gray-90'],
  'font-secondary': p['gray-60'],
  'font-tertiary': p['navy-80'],
  'font-link': p['blue-60'],
  'font-link-hover': p['blue-70'],
  'font-success': p['green-60'],
  'font-danger': p['red-60'],
  'font-inverse': p.white,
  'font-disabled': p['gray-60'],
  border: p['gray-30'],
  'border-secondary': p['gray-20'],
  'border-tertiary': p['blue-60'],
  'border-selected': p['navy-80'],
  'border-highlight': p['yellow-50'],
  'border-danger-weak': p['red-30'],
  'border-danger-strong': p['red-60'],
  'border-contrast': p['gray-40'],
  'border-inverse': p.white,
  'outline-weak': p['blue-30'],
  'outline-moderate': p['blue-60'],
  'outline-extreme': p['gray-90'],
  'set-1-3': p['yellow-40'],
  'set-1-5': p['purple-40'],
  'set-2-2': p['gray-60'],
  'set-3-4': p['green-60'],
  'set-3-6': p['yellow-60'],
  'data-solid-navy': p['navy-80'],
  'data-solid-coral': p['coral-50'],
  'data-solid-purple': p['purple-50'],
  'data-solid-green': p['green-50'],
  'data-solid-blue': p['blue-50'],
  'data-solid-periwinkle': p['periwinkle-40'],
  'data-solid-gray': p['gray-50'],
  'data-solid-background-gray': p['gray-20'],
  'surface-spheric-background': alpha(p.white, exoOpacity['8']),
  'surface-spheric-brand': alpha(p['navy-80'], exoOpacity['8']),
  'surface-spheric-success': alpha(p['green-10'], exoOpacity['8']),
  'surface-spheric-warning': alpha(p['yellow-20'], exoOpacity['8']),
  'surface-spheric-danger-weak': alpha(p['red-10'], exoOpacity['8']),
  'shadow-weak': alpha(p.black, exoOpacity['2']),
  'shadow-moderate': alpha(p.black, exoOpacity['2']),
  'shadow-blue-outline-200': `${alpha(
    p['blue-50'],
    exoOpacity['2'],
  )} 0 2px 4px 0`,
  'shadow-blue-outline-300': `${alpha(
    p['blue-50'],
    exoOpacity['2'],
  )} 0 4px 8px 0`,
  'shadow-red-outline-300': `${alpha(
    p['red-50'],
    exoOpacity['2'],
  )} 0 4px 8px 0`,
  'shadow-green-outline-500': `${alpha(
    p['green-50'],
    exoOpacity['2'],
  )} 0 16px 32px 0`,
  'shadow-yellow-outline-500': `${alpha(
    p['yellow-50'],
    exoOpacity['2'],
  )} 0 16px 32px 0`,
  'shadow-red-outline-500': `${alpha(
    p['red-50'],
    exoOpacity['2'],
  )} 0 16px 32px 0`,
};

export type ExoColorToken = keyof typeof exoColorsLight;

const exoColorsDark: Record<ExoColorToken, string> = {
  page: p['gray-100'],
  background: p['gray-80'],
  'background-secondary': p['gray-90'],
  'background-tertiary': p['gray-100'],
  'background-tertiary-selected-weak': p['gray-70'],
  'background-brand': p['navy-30'],
  'background-action': p['navy-30'],
  'background-action-hover': p['navy-10'],
  'background-action-hover-weak': p['gray-70'],
  'background-action-inverse': p.white,
  'background-action-secondary': p['gray-90'],
  'background-action-secondary-hover': p['blue-90'],
  'background-disabled': p['gray-60'],
  'background-deselected': p['navy-30'],
  'background-selected': p['blue-30'],
  'background-selected-hover': p['blue-20'],
  'background-selected-weak': p['navy-70'],
  'background-highlight': p['coral-70'],
  'background-success': p['green-90'],
  'background-success-strong': p['green-40'],
  'background-warning': p['yellow-80'],
  'background-warning-strong': p['yellow-40'],
  'background-danger-weak': p['red-90'],
  'background-danger-strong': p['red-50'],
  'background-danger-strong-hover': p['red-30'],
  'background-info-strong': p['gray-20'],
  icon: p['blue-30'],
  'icon-disabled': p['gray-30'],
  'icon-inverse': p['gray-90'],
  font: p.white,
  'font-secondary': p['gray-30'],
  'font-tertiary': p['blue-30'],
  'font-link': p['blue-30'],
  'font-link-hover': p['blue-20'],
  'font-success': p['green-50'],
  'font-danger': p['red-50'],
  'font-inverse': p['gray-90'],
  'font-disabled': p['gray-30'],
  border: p['gray-60'],
  'border-secondary': p['gray-70'],
  'border-tertiary': p['blue-50'],
  'border-selected': p['navy-30'],
  'border-highlight': p['yellow-60'],
  'border-danger-weak': p['red-70'],
  'border-danger-strong': p['red-50'],
  'border-contrast': p['gray-50'],
  'border-inverse': p['gray-90'],
  'outline-weak': p['blue-80'],
  'outline-moderate': p['blue-50'],
  'outline-extreme': p.white,
  'set-1-3': p['yellow-60'],
  'set-1-5': p['purple-60'],
  'set-2-2': p['gray-50'],
  'set-3-4': p['green-50'],
  'set-3-6': p['yellow-40'],
  'data-solid-navy': p['navy-50'],
  'data-solid-coral': p['coral-50'],
  'data-solid-purple': p['purple-40'],
  'data-solid-green': p['green-50'],
  'data-solid-blue': p['blue-40'],
  'data-solid-periwinkle': p['periwinkle-50'],
  'data-solid-gray': p['gray-50'],
  'data-solid-background-gray': p['gray-70'],
  'surface-spheric-background': alpha(p['gray-80'], exoOpacity['8']),
  'surface-spheric-brand': alpha(p['navy-30'], exoOpacity['8']),
  'surface-spheric-success': alpha(p['green-90'], exoOpacity['8']),
  'surface-spheric-warning': alpha(p['yellow-80'], exoOpacity['8']),
  'surface-spheric-danger-weak': alpha(p['red-90'], exoOpacity['8']),
  // The stylesheet's dark value is a bare opacity, not a color. Black at
  // opacity-3 matches its dark shadow-100 to shadow-500.
  'shadow-weak': alpha(p.black, exoOpacity['3']),
  'shadow-moderate': alpha(p.black, exoOpacity['5']),
  'shadow-blue-outline-200': `${alpha(
    p['blue-50'],
    exoOpacity['2-1'],
  )} 0 2px 4px 0`,
  'shadow-blue-outline-300': `${alpha(
    p['blue-50'],
    exoOpacity['2-1'],
  )} 0 4px 8px 0`,
  'shadow-red-outline-300': `${alpha(
    p['red-50'],
    exoOpacity['2-1'],
  )} 0 4px 8px 0`,
  'shadow-green-outline-500': `${alpha(
    p['green-50'],
    exoOpacity['2-1'],
  )} 0 16px 32px 0`,
  'shadow-yellow-outline-500': `${alpha(
    p['yellow-50'],
    exoOpacity['2-1'],
  )} 0 16px 32px 0`,
  'shadow-red-outline-500': `${alpha(
    p['red-50'],
    exoOpacity['2-1'],
  )} 0 16px 32px 0`,
};

export const exoColors: Record<PaletteMode, Record<ExoColorToken, string>> = {
  light: exoColorsLight,
  dark: exoColorsDark,
};

// Roles Exosphere has no token for, named as in the Figma `Exo · Theme`
// collection. Each dark value copies the pairing Exosphere uses for a similar
// role. Replace them with Boomi-approved tokens (APP-1835).
const derivedColorsLight = {
  'derived/neutral-weak': p['gray-40'],
  'derived/neutral-faint': p['gray-30'],
  'derived/font-warning': p['yellow-60'],
  'derived/border-on-brand': p['navy-60'],
  'derived/gridline': alpha(p['gray-30'], exoOpacity['4']),
  // Chart marks for warning and failure, matching `data-solid-green`: the
  // strong status backgrounds are made to carry white text and read too dark
  // as rings and dots. Exosphere has no yellow or red data token.
  'derived/data-warning': p['yellow-40'],
  'derived/data-danger': p['red-50'],
  'highlight/selected': alpha(p['blue-60'], exoOpacity['1']),
  'highlight/selected-hover': alpha(p['blue-60'], exoOpacity['2']),
  scrim: alpha(p['gray-90'], exoOpacity['3']),
};

export type DerivedColorToken = keyof typeof derivedColorsLight;

const derivedColorsDark: Record<DerivedColorToken, string> = {
  'derived/neutral-weak': p['gray-50'],
  'derived/neutral-faint': p['gray-60'],
  'derived/font-warning': p['yellow-40'],
  'derived/border-on-brand': p['navy-40'],
  'derived/gridline': alpha(p['gray-60'], exoOpacity['4']),
  'derived/data-warning': p['yellow-40'],
  'derived/data-danger': p['red-50'],
  'highlight/selected': alpha(p['blue-30'], exoOpacity['1']),
  'highlight/selected-hover': alpha(p['blue-30'], exoOpacity['2']),
  scrim: alpha(p['gray-90'], exoOpacity['5']),
};

export const derivedColors: Record<
  PaletteMode,
  Record<DerivedColorToken, string>
> = {
  light: derivedColorsLight,
  dark: derivedColorsDark,
};

// Elementary brand exceptions: AI and the mascot stay Elementary pink. The
// accents and gradient stops are the same in both modes; tinted surfaces
// become the accent at an opacity step in dark mode.
const AI_ACCENT = '#FF20B8';

const aiColorsLight = {
  'ai/accent': AI_ACCENT,
  'ai/accent-soft': '#FC9EFA',
  'ai/accent-lilac': '#E4ABFC',
  'ai/accent-muted': '#B598CA',
  'ai/surface-weak': '#FFE9FD',
  'ai/surface-subtle': '#FFF4FB',
  'ai/surface-faint': '#FEF0FE',
  'ai/gradient-pink': '#F9C4FF',
  'ai/gradient-rose': '#FBC2EB',
  'ai/gradient-blue': '#A1C4FD',
};

export type AiColorToken = keyof typeof aiColorsLight;

export const aiColors: Record<PaletteMode, Record<AiColorToken, string>> = {
  light: aiColorsLight,
  dark: {
    ...aiColorsLight,
    'ai/surface-weak': alpha(AI_ACCENT, exoOpacity['2']),
    'ai/surface-subtle': alpha(AI_ACCENT, exoOpacity['1']),
    'ai/surface-faint': alpha(AI_ACCENT, exoOpacity['1']),
  },
};

export const exoShadowOffsets = {
  '100': '0 1px 2px',
  '200': '0 2px 4px',
  '300': '0 4px 8px',
  '400': '0 8px 16px',
  '500': '0 16px 32px',
};

export const exoSpacing = {
  '4x-small': 1,
  '3x-small': 2,
  '2x-small': 4,
  'x-small': 8,
  small: 12,
  standard: 16,
  medium: 20,
  large: 24,
  'x-large': 32,
  '2x-large': 48,
  '3x-large': 64,
  '4x-large': 72,
  '5x-large': 96,
};

export const exoRadius = {
  small: 2,
  standard: 5,
  large: 10,
  'x-large': 25,
};

export const EXO_FONTS_STYLESHEET =
  'https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&family=Fira+Mono:wght@400;500;700&display=swap';

export const exoFonts = {
  family: "'Noto Sans', sans-serif",
  brand: "'Poppins', sans-serif",
  monospace: "'Fira Mono', monospace",
  weightRegular: 400,
  weightSemiBold: 600,
  weightBold: 700,
};

// Exosphere motion: durations in ms and easing curves.
export const exoMotion = {
  'time-fast': 100,
  'time-standard': 200,
  'time-medium': 240,
  'time-moderate': 320,
  'curve-standard': 'cubic-bezier(0.2, 0, 0, 1)',
  'curve-decelerate': 'cubic-bezier(0, 0, 0.2, 1)',
  'curve-accelerate': 'cubic-bezier(0.4, 0, 1, 1)',
};

export const exoFontSizes = {
  'x-micro': 10,
  micro: 12,
  'x-small': 13,
  small: 14,
  medium: 16,
  large: 18,
  'x-large': 20,
};

// The Exosphere text styles our content text maps to, named as in
// Exosphere. `label-x-large-bold` is `text-label-medium-bold` at
// `font-size-x-large`, as the Exosphere EmptyState label and spaced Dialog
// title draw it. Bold is our semi-bold, as everywhere in the Boomi looks.
export const exoTextStyles = {
  'label-micro': {
    fontSize: `${exoFontSizes.micro}px`,
    lineHeight: 1.5,
    fontWeight: exoFonts.weightRegular,
  },
  'body-medium': {
    fontSize: `${exoFontSizes.small}px`,
    lineHeight: 1.5,
    fontWeight: exoFonts.weightRegular,
  },
  'label-x-large-bold': {
    fontSize: `${exoFontSizes['x-large']}px`,
    lineHeight: 1.5,
    fontWeight: exoFonts.weightSemiBold,
  },
};
